import { useIsMutating, useMutation } from '@tanstack/react-query';
import { useToast } from 'components/Providers/ToastProvider';
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import useCompetitorsStore from 'store/useCompetitorsStore';
import { useOnboardingStore } from 'store/useOnboardingStore';
import useUserStore from 'store/useUserStore';
import { ROUTES } from 'util/Constants';
import { isValidWebsiteUrl } from 'util/isValidWebsiteUrl';
import { BlackButton } from '../auth';
import IntroduceSection from './Introduce';
import SelectPlanSection from './SelectPlan';
import OnboardingStepper from './Stepper';

const OnboardingContent = () => {
    const { currentStep, setCurrentStep, saveOnboardingInfo, account } = useOnboardingStore();
    const { userInfo, isOnboardingUser } = useUserStore();
    const { showMessage } = useToast();

    useEffect(() => {
        // If user has a subscription, skip to Account Setup step
        if (userInfo?.subscriptionId) {
            setCurrentStep(1);
        }
    }, [userInfo, setCurrentStep]);

    const steps = [
        { title: 'Select Plan', component: <SelectPlanSection /> },
        { title: 'Account Setup', component: <IntroduceSection /> },
    ];

    const { mutate: mutateSaveOnboarding } = useMutation({
        mutationKey: ['saveOnboardingInfo'],
        mutationFn: saveOnboardingInfo,
        onError: (error) => {
            showMessage('Error', error.message);
        },
        onSuccess: () => {
            setCurrentStep(currentStep + 1);
        },
    });

    const handleSaveOnboardingInfo = () => {
        if (!account.referralSource) {
            showMessage('Alert', 'Please Select where did you hear about us!');
            return;
        }
        if (!isValidWebsiteUrl(account.websiteUrl)) {
            showMessage('Alert', 'Please enter a valid website URL!');
            return;
        }
        mutateSaveOnboarding();
    };

    // Move navigation checks after all hooks are defined
    if (!isOnboardingUser()) {
        return <Navigate to={ROUTES.MAIN} replace />;
    }

    if (currentStep >= steps.length) {
        return <Navigate to={ROUTES.MAIN} />;
    }

    return (
        <div className="lg:pb-[30px] pb-[60px]">
            <div className="sticky top-0 z-20 bg-white h-[56px] border-b border-my-gray border-opacity-60">
                <OnboardingStepper titles={steps.map((step) => step.title)} completedIndex={currentStep} />
            </div>

            <div className="lg:max-w-hello lg:mx-auto mx-[10px] mt-8">{steps[currentStep].component}</div>
        </div>
    );
};

export default OnboardingContent;
